import { createSlice } from "@reduxjs/toolkit"
import { EAccountRole } from "enums/commonEnums"
import { EComplaintLinkSource, IComplaintSummary } from "pages/Complaints/types/complaintLogistics"
import { DropdownObj } from "pages/Common/commonTypes"
import { SubmitFileInfo } from "pages/Common/fileUploadService"

export interface ISaasFranchiseDetails {
    franchise_name: string
    saas_fee_ac: string
    saas_fee_dc: string
    host_managed_by: DropdownObj | null
    tenure: string
    state: DropdownObj | null
    city: DropdownObj | null
    agreement_start_date: string
    agreement_end_date: string
    agreement_url: string | null
}

export interface IDealDetailsForm {
    deal_type: {
        id: string
        title: string
    }
    deal_scope_of_work: DropdownObj[]
    template_id: DropdownObj
}

export interface IAccountRoleData {
    role: EAccountRole
    account_type_id: number
    account_id: number
    billing_address_id: number
    contact_id: number
    contact_type_id: number
}

export interface ISitePrefill {
    id: number
    name: string
    site_id: string
    pincode: number
    address: string
    state: string
    state_id: number
    city: string
    city_id: number
    country_id: number
    latitude: number
    longitude: number
}

export interface IAccountNSitesForm {
    destination_site_id: number | null
    source_site_id: number | null
    // Full site objects resolved from the project-details response, used to prefill
    // the selection in edit mode without depending on the paginated sites list.
    destination_site?: ISitePrefill | null
    source_site?: ISitePrefill | null
    account_roles: IAccountRoleData[]
    is_franchise_partner?: boolean
    franchise_mapping_date?: string | null
}

export interface IAccountNSitesFormValues {
    selectedRoles: Array<{ id: string | number; title: string; disabled?: boolean }>
    accountRoles: Record<
        string,
        {
            account: { id: string | number; title: string } | null
            billing_address: { id: string | number; title: string } | null
            contact: { id: string | number; title: string } | null
            contact_type_id: number | null
        }
    >
    destinationSite: ISitePrefill | null
    sourceSite: ISitePrefill | null
    is_franchise_partner?: boolean
    franchise_mapping_date?: string | null
}

export interface IProjectBasicDetailsForm {
    project_name: string
    deal_done_by_id: DropdownObj | null
    project_closure_date: string
    program_id: DropdownObj | null
    category_id: DropdownObj | null
    purchase_order: DropdownObj | null
    deal_docs?: string | null
}

export interface ILogisticBasicDetailsForm {
    project_name: string
    category_id: DropdownObj | null
    purchase_order: DropdownObj | null
    visit_by_date: string
    visit_by_time: DropdownObj | null
    source_poc?: Partial<IPocDetailsForm>
    destination_poc?: Partial<IPocDetailsForm>
    /** The complaint this dispatch was raised for. Only ever set for a complaint-linked category. */
    complaint_id?: DropdownObj | null
    /** Identifying fields of the referenced complaint, kept so the read-only summary card
     *  survives navigating back to this step. */
    complaint_summary?: IComplaintSummary | null
    /** Which surface created the link — stamped on submit, reporting only. */
    complaint_link_source?: EComplaintLinkSource | null
}

export interface ILandDealForm {
    host_account: DropdownObj | null
    property_type: { id: string; title: string } | null
    connection_type: { id: string; title: string } | null
    sanctioned_load: string
    dg_connection_used: DropdownObj | null
    dg_unit_rate: string
    parking_slots: string
    parking_type: DropdownObj | null
    vehicle_movement: DropdownObj[]
    deal_amount: string
    license_fee_ac: string
    license_fee_dc: string
    incremental: DropdownObj | null
    incremental_percent: string
    incremental_duration: DropdownObj | null
    land_size: string | null
}

export interface IInfraForm {
    sow_group_ids: DropdownObj[]
    survey_id: DropdownObj | null
    is_material_needed: DropdownObj | null
    ocpp_url: string
    port_number: string
    supplied_by_statiq: DropdownObj | null
    select_project: DropdownObj | null
    select_chargers_to_add: DropdownObj[]
    eva_required: DropdownObj | null
    connection_owned_by: { id: string; title: string } | null
    infra_remarks: string
    charger_details: Array<{
        id: string
        sku_code: string
        display_name: string
        cin: string
        ocpp_id: string
        remarks: string
        product_id?: number
        project_linked_product_id?: number
    }>
}

export interface IChargersAndMaterialsForm {
    chargers: Array<{
        sku_code: string
        display_name: string
        power_rating: number
        branding: number
        product_id?: number
    }>
    materials: Array<{
        sku_code: string
        description: string
        quantity: number
        remarks: string
        product_id?: number
    }>
}

export interface IAddOnsForm {
    is_warranty_applicable: boolean
    warranty_duration_in_months: string
    warranty_start_criteria: DropdownObj | null
    warranty_start_date: string
    is_amc_applicable: boolean
    amc_type: DropdownObj | null
    amc_start_date: string
    amc_duration: string
    is_manpower_applicable: boolean
    manpower_marshal_enabled: boolean
    manpower_marshal_resource: string
    manpower_marshal_duration: string
    manpower_guard_enabled: boolean
    manpower_guard_resource: string
    manpower_guard_duration: string
    manpower_customer_support_enabled: boolean
    manpower_customer_support_resource: string
    manpower_customer_support_duration: string
    charger_connectivity_required: boolean
    charger_connection_type: DropdownObj | null
    sim_duration: string
    is_o_and_m_required: boolean
    insurance_required: boolean
}

export interface IPocDetailsForm {
    on_ground_poc_name: string
    on_ground_poc_phone: string
    on_ground_poc_email: string
}

export interface ISaasForm {
    cpo: DropdownObj | null
    csms_partner: DropdownObj | null
    saas_fee: boolean
    license_fee_ac: string
    license_fee_dc: string
    station_name: string
    area: string
    country: DropdownObj | null
    state: DropdownObj | null
    city: DropdownObj | null
    pincode: string
    landmark: string
    latitude: string
    longitude: string
    amenities: DropdownObj[] | null
    access_type: DropdownObj | null
    co_branding_enabled: boolean
    co_branding_partner: DropdownObj | null
    opening_hours: DropdownObj | null
    location_type: DropdownObj | null
    location_subtype: DropdownObj | null
    franchise?: ISaasFranchiseDetails | null
}

export interface IPaymentDetailsForm {
    total_amount: string
    specify_payment_terms: DropdownObj
    payment_terms: boolean
    payment_terms_obj: any[]
}

export interface IDemolitionMaterialsForm {
    is_site_demolition_req: boolean
    plp_ids: number[]
}

export interface IDemolitionPendingDealDocs {
    file_type_id: string
    files: File[]
}

export interface ISurveyBasicDetailsForm {
    survey_name: string
    category_id: DropdownObj | null
    business_poc: DropdownObj | null
    charger_details: Array<{
        charger_type: DropdownObj | null
        power_rating: string
        quantity: string
    }>
    available_load: string
    connection_type: DropdownObj | null
    connection_owned_by: DropdownObj | null
    no_of_spots: string
    visit_by_date: string
    time_slot: DropdownObj | null
    foundation_type: DropdownObj | null
    remarks: string
}

interface IProjectFormState {
    deal_details_form: IDealDetailsForm
    account_n_sites_form: IAccountNSitesForm
    account_n_sites_form_values: IAccountNSitesFormValues | null
    project_id: number | null
    project_basic_details_form: IProjectBasicDetailsForm | null
    logistics_basic_details_form: ILogisticBasicDetailsForm | null
    land_deal_form: ILandDealForm | null
    infra_form: IInfraForm | null
    chargers_and_materials_form: IChargersAndMaterialsForm | null
    payment_details_form: IPaymentDetailsForm
    add_ons_form: IAddOnsForm | null
    poc_details_form: IPocDetailsForm | null
    saas_form: ISaasForm | null
    survey_basic_details_form: ISurveyBasicDetailsForm | null
    demolition_materials_form: IDemolitionMaterialsForm | null
    survey_pending_file_infos: SubmitFileInfo[]
    survey_spot_image_names: string[]
    survey_electricity_bill_names: string[]
    survey_spot_image_infos: SubmitFileInfo[]
    survey_electricity_bill_infos: SubmitFileInfo[]
    survey_pending_spot_files: File[]
    survey_pending_electricity_bill_files: File[]
    demolition_pending_deal_docs: IDemolitionPendingDealDocs | null
    /**
     * Set when the form is opened from a complaint drawer via "Add Logistic". Its presence is what
     * locks Project Category and Complaint Reference ID on the Project Details step — there is no
     * in-modal way to switch either, so a dispatch can never be filed against the wrong complaint.
     */
    complaint_link_context: IComplaintSummary | null
}

const initialState: IProjectFormState = {
    deal_details_form: {
        deal_type: {} as {
            id: ""
            title: ""
        },
        deal_scope_of_work: [],
        template_id: {} as DropdownObj,
    },
    account_n_sites_form: {
        destination_site_id: null,
        source_site_id: null,
        account_roles: [],
    },
    account_n_sites_form_values: null,
    project_id: null,
    project_basic_details_form: null,
    logistics_basic_details_form: null,
    land_deal_form: null,
    infra_form: null,
    chargers_and_materials_form: null,
    payment_details_form: {
        total_amount: "",
        specify_payment_terms: { id: 0, title: "" },
        payment_terms: false,
        payment_terms_obj: [],
    },
    add_ons_form: null,
    poc_details_form: null,
    saas_form: null,
    survey_basic_details_form: null,
    demolition_materials_form: null,
    survey_pending_file_infos: [],
    survey_spot_image_names: [],
    survey_electricity_bill_names: [],
    survey_spot_image_infos: [],
    survey_electricity_bill_infos: [],
    survey_pending_spot_files: [],
    survey_pending_electricity_bill_files: [],
    demolition_pending_deal_docs: null,
    complaint_link_context: null,
}

const ProjectFormSlice = createSlice({
    name: "ProjectForm",
    initialState: initialState,
    reducers: {
        setDealDetailsForm: (state, { payload }: { payload: IDealDetailsForm }) => {
            state.deal_details_form = payload
        },
        setAccountNSitesForm: (state, { payload }: { payload: IAccountNSitesForm }) => {
            state.account_n_sites_form = payload
        },
        setProjectFormDestinationSite: (state, { payload }: { payload: number }) => {
            state.account_n_sites_form.destination_site_id = payload
        },
        setAccountNSitesFormValues: (state, { payload }: { payload: IAccountNSitesFormValues }) => {
            state.account_n_sites_form_values = payload
        },
        setProjectId: (state, { payload }: { payload: number }) => {
            state.project_id = payload
        },
        setProjectBasicDetailsForm: (state, { payload }: { payload: IProjectBasicDetailsForm }) => {
            state.project_basic_details_form = payload
        },
        setLogisticsBasicDetailsForm: (state, { payload }: { payload: ILogisticBasicDetailsForm }) => {
            state.logistics_basic_details_form = payload
        },
        setLandDealForm: (state, { payload }: { payload: ILandDealForm }) => {
            state.land_deal_form = payload
        },
        setInfraForm: (state, { payload }: { payload: IInfraForm }) => {
            state.infra_form = payload
        },
        setChargersAndMaterialsForm: (state, { payload }: { payload: IChargersAndMaterialsForm }) => {
            state.chargers_and_materials_form = payload
        },
        setPaymentDetailsForm: (state, { payload }: { payload: IPaymentDetailsForm }) => {
            state.payment_details_form = payload
        },
        setPaymentDetailsFormValue: (
            state,
            { payload }: { payload: { value: DropdownObj | string | boolean | any[]; key: keyof IPaymentDetailsForm } }
        ) => {
            ;(state.payment_details_form as any)[payload.key] = payload.value
        },
        setAddOnsForm: (state, { payload }: { payload: IAddOnsForm }) => {
            state.add_ons_form = payload
        },
        setPocDetailsForm: (state, { payload }: { payload: IPocDetailsForm }) => {
            state.poc_details_form = payload
        },
        setDemolitionMaterialsForm: (state, { payload }: { payload: IDemolitionMaterialsForm }) => {
            state.demolition_materials_form = payload
        },
        setSurveyBasicDetailsForm: (state, { payload }: { payload: ISurveyBasicDetailsForm }) => {
            state.survey_basic_details_form = payload
        },
        setSurveyPendingFileInfos: (state, { payload }: { payload: SubmitFileInfo[] }) => {
            state.survey_pending_file_infos = payload
        },
        setSurveySpotImageNames: (state, { payload }: { payload: string[] }) => {
            state.survey_spot_image_names = payload
        },
        setSurveyElectricityBillNames: (state, { payload }: { payload: string[] }) => {
            state.survey_electricity_bill_names = payload
        },
        setSurveySpotImageInfos: (state, { payload }: { payload: SubmitFileInfo[] }) => {
            state.survey_spot_image_infos = payload
        },
        setSurveyElectricityBillInfos: (state, { payload }: { payload: SubmitFileInfo[] }) => {
            state.survey_electricity_bill_infos = payload
        },
        setSurveyPendingSpotFiles: (state, { payload }: { payload: File[] }) => {
            state.survey_pending_spot_files = payload
        },
        setSurveyPendingElectricityBillFiles: (state, { payload }: { payload: File[] }) => {
            state.survey_pending_electricity_bill_files = payload
        },
        setDemolitionPendingDealDocs: (state, { payload }: { payload: IDemolitionPendingDealDocs | null }) => {
            state.demolition_pending_deal_docs = payload
        },
        setSaasForm: (state, { payload }: { payload: ISaasForm }) => {
            state.saas_form = payload
        },
        setComplaintLinkContext: (state, { payload }: { payload: IComplaintSummary | null }) => {
            state.complaint_link_context = payload
        },
        resetProjectForm: state => {
            state.deal_details_form = initialState.deal_details_form
            state.account_n_sites_form = initialState.account_n_sites_form
            state.account_n_sites_form_values = initialState.account_n_sites_form_values
            state.project_id = initialState.project_id
            state.project_basic_details_form = initialState.project_basic_details_form
            state.land_deal_form = initialState.land_deal_form
            state.infra_form = initialState.infra_form
            state.chargers_and_materials_form = initialState.chargers_and_materials_form
            state.payment_details_form = initialState.payment_details_form
            state.add_ons_form = initialState.add_ons_form
            state.poc_details_form = initialState.poc_details_form
            state.saas_form = initialState.saas_form
            state.logistics_basic_details_form = initialState.logistics_basic_details_form
            state.survey_basic_details_form = initialState.survey_basic_details_form
            state.survey_pending_file_infos = initialState.survey_pending_file_infos
            state.survey_spot_image_names = initialState.survey_spot_image_names
            state.survey_electricity_bill_names = initialState.survey_electricity_bill_names
            state.survey_spot_image_infos = initialState.survey_spot_image_infos
            state.survey_electricity_bill_infos = initialState.survey_electricity_bill_infos
            state.survey_pending_spot_files = initialState.survey_pending_spot_files
            state.survey_pending_electricity_bill_files = initialState.survey_pending_electricity_bill_files
            state.demolition_pending_deal_docs = initialState.demolition_pending_deal_docs
            state.complaint_link_context = initialState.complaint_link_context
        },
        resetProjectFormOnTemplateChange: state => {
            state.land_deal_form = initialState.land_deal_form
            state.infra_form = initialState.infra_form
            state.chargers_and_materials_form = initialState.chargers_and_materials_form
            state.payment_details_form = initialState.payment_details_form
            state.add_ons_form = initialState.add_ons_form
            state.poc_details_form = initialState.poc_details_form
            state.saas_form = initialState.saas_form
        },
    },
})

export const {
    setDealDetailsForm,
    setAccountNSitesForm,
    setAccountNSitesFormValues,
    setProjectId,
    setProjectBasicDetailsForm,
    setLogisticsBasicDetailsForm,
    setLandDealForm,
    setInfraForm,
    setChargersAndMaterialsForm,
    setPaymentDetailsForm,
    setPaymentDetailsFormValue,
    setAddOnsForm,
    setPocDetailsForm,
    setDemolitionMaterialsForm,
    setSurveyBasicDetailsForm,
    setSurveyPendingFileInfos,
    setSurveySpotImageNames,
    setSurveyElectricityBillNames,
    setSurveySpotImageInfos,
    setSurveyElectricityBillInfos,
    setSurveyPendingSpotFiles,
    setSurveyPendingElectricityBillFiles,
    setDemolitionPendingDealDocs,
    setSaasForm,
    setComplaintLinkContext,
    resetProjectForm,
    resetProjectFormOnTemplateChange,
    setProjectFormDestinationSite,
} = ProjectFormSlice.actions
export default ProjectFormSlice.reducer
